import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES, PROCESS_STEPS } from "@/constants/site";

const EASE = [0.16, 1, 0.3, 1];

export default function Process() {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="processus" className="relative py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="mb-12 md:mb-16"
        >
          <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.3em] text-fuchsia-400">02 — Savoir-faire</p>
          <h2 className="max-w-2xl font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
            De l'idée à la <span className="text-holo">pièce finie</span>, en 4 étapes
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {PROCESS_STEPS.map((step, i) => (
            <motion.div
              key={step.num}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.6, ease: EASE, delay: i * 0.1 }}
              data-testid={`process-step-${i + 1}`}
              className="group relative overflow-hidden rounded-2xl border border-white/10 bg-[#100e19] p-7 transition-colors duration-500 hover:border-cyan-400/40"
            >
              <span className="font-mono text-4xl font-bold text-white/10 transition-colors duration-500 group-hover:text-cyan-400/30">
                {step.num}
              </span>
              <h3 className="font-display mt-5 text-lg font-bold text-white">{step.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-[#a09bb5]">{step.desc}</p>
              <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-gradient-to-r from-cyan-400 via-fuchsia-500 to-amber-400 transition-all duration-500 group-hover:w-full" />
            </motion.div>
          ))}
        </div>

        <motion.div
          ref={imgRef}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="relative mt-14 overflow-hidden rounded-3xl border border-white/10"
          data-testid="process-atelier-banner"
        >
          <motion.img
            src={IMAGES.atelier}
            alt="Peinture à la main d'une carte personnalisée dans l'atelier"
            style={{ y, scale: 1.16 }}
            className="h-[300px] w-full object-cover md:h-[420px]"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#08070d]/90 via-[#08070d]/30 to-transparent" />
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-md p-8 md:p-14">
              <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.3em] text-amber-300">L'atelier</p>
              <h3 className="font-display text-2xl font-extrabold leading-tight text-white sm:text-3xl">
                Le décor du tableau reprend exactement l'illustration de votre carte
              </h3>
              <p className="mt-4 text-sm font-medium leading-relaxed text-white md:text-base">
                Une carte 100% personnalisée, montée au centre sous boîtier acrylique transparent,
                sur un fond qui prolonge l'œuvre. Impression premium sur trois papiers sélectionnés.
                Format 20 × 20 cm, prêt à accrocher.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
